import { FileText, FileType, FileType2, Loader2, Recycle, X } from 'lucide-react'
import { useI18n } from '../lib/i18n'
import type { AttachedDoc } from '../lib/types'

/**
 * Three small chip-shaped UI primitives the Composer composes together
 * to render the (zero-to-many) attachment area above the textarea:
 *
 *   - `AttachmentChip`         — one successfully-uploaded document
 *   - `AttachmentUploadingChip` — the in-flight upload (with X / Y progress)
 *   - `AttachmentErrorChip`    — the most recent upload failure
 *
 * Splitting them keeps each component single-purpose; the Composer
 * decides ordering, layout, and which combination is visible.
 */

interface ChipProps {
  doc: AttachedDoc
  onRemove: () => void
  /** Composer disables removal while the model is mid-stream so the
   *  attachment cannot disappear out from under an active request. */
  disabled?: boolean
}

/** One successfully-uploaded attachment.  Always renders something —
 *  the parent decides whether to mount it. */
export function AttachmentChip({ doc, onRemove, disabled }: ChipProps) {
  const { s } = useI18n()
  const Icon = iconForKind(doc.kind)
  return (
    <ChipShell tone="neutral">
      <Icon className="w-4 h-4 text-brand-600 flex-shrink-0" />
      <div className="flex-1 min-w-0 flex items-baseline gap-2">
        <span
          className="text-sm font-medium text-brand-800 truncate"
          title={doc.filename}
        >
          {doc.filename}
        </span>
        <span className="text-[11px] text-brand-500 flex-shrink-0 whitespace-nowrap">
          {describeDoc(doc, s.attachmentPages, s.attachmentChars, s.attachmentTruncated)}
        </span>
      </div>
      {doc.deduped && (
        <span
          className="flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[10px] font-medium flex-shrink-0"
          title={s.attachmentDuplicateTitle}
        >
          <Recycle className="w-3 h-3" aria-hidden />
          {s.attachmentDuplicate}
        </span>
      )}
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        className="text-brand-500 hover:text-brand-800 disabled:text-brand-300 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
        aria-label={s.attachmentRemove}
        title={s.attachmentRemove}
      >
        <X className="w-4 h-4" />
      </button>
    </ChipShell>
  )
}

interface UploadingProps {
  filename: string
  size_bytes: number
  /** 1-based position within the current batch, or `undefined` when a
   *  single file is being uploaded (no "1 of 1" noise). */
  index?: number
  total?: number
  /** Optional cancel handler — if provided, an × button is shown. */
  onCancel?: () => void
}

/** In-flight upload state, with optional "N of M" progress badge. */
export function AttachmentUploadingChip({
  filename,
  size_bytes,
  index,
  total,
  onCancel,
}: UploadingProps) {
  const { s } = useI18n()
  const showProgress = typeof index === 'number' && typeof total === 'number' && total > 1
  return (
    <ChipShell tone="neutral">
      <Loader2 className="w-4 h-4 text-brand-500 animate-spin flex-shrink-0" />
      <span className="text-sm text-brand-700 truncate flex-1 min-w-0">
        {showProgress
          ? s.attachmentUploadingN(index!, total!)
          : s.attachmentUploading}
        : <span className="font-medium">{filename}</span>
      </span>
      <span className="text-[11px] text-brand-400 flex-shrink-0">
        {formatBytes(size_bytes)}
      </span>
      {onCancel && (
        <button
          type="button"
          onClick={onCancel}
          className="text-brand-500 hover:text-brand-800 flex-shrink-0 transition-colors"
          aria-label={s.attachmentRemove}
          title={s.attachmentRemove}
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </ChipShell>
  )
}

interface ErrorProps {
  error: string
  onDismiss: () => void
}

/** A dismissable red chip for the most recent upload failure. */
export function AttachmentErrorChip({ error, onDismiss }: ErrorProps) {
  const { s } = useI18n()
  return (
    <ChipShell tone="error">
      <span className="text-sm text-red-700 truncate flex-1 min-w-0">
        <span className="font-semibold">{s.attachmentFailed}:</span> {error}
      </span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-red-600 hover:text-red-800 flex-shrink-0"
        aria-label={s.attachmentRemove}
        title={s.attachmentRemove}
      >
        <X className="w-4 h-4" />
      </button>
    </ChipShell>
  )
}

function ChipShell({
  tone,
  children,
}: {
  tone: 'neutral' | 'error'
  children: React.ReactNode
}) {
  const cls =
    tone === 'error'
      ? 'border-red-200 bg-red-50'
      : 'border-brand-200 bg-brand-50/70'
  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 border rounded-lg ${cls}`}
    >
      {children}
    </div>
  )
}

function iconForKind(kind: AttachedDoc['kind']) {
  switch (kind) {
    case 'pdf':
      return FileType2 // PDF
    case 'docx':
      return FileType // Word
    default:
      return FileText // Plain text / Markdown
  }
}

function describeDoc(
  doc: AttachedDoc,
  pagesLabel: (n: number) => string,
  charsLabel: (n: number) => string,
  truncatedLabel: string,
): string {
  const parts: string[] = []
  if (doc.kind === 'pdf' && doc.n_pages) parts.push(pagesLabel(doc.n_pages))
  parts.push(formatBytes(doc.size_bytes))
  parts.push(charsLabel(doc.text_chars))
  if (doc.truncated) parts.push(truncatedLabel)
  return parts.join(' · ')
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}
